import React from 'react';
import { Col, Row, Form, Button, ToggleButton, ButtonGroup } from '../../ui-kit';
import PeriodSelector from '../../../utils/forms/PeriodSelector';
import ActivityTypeSelector from '../../general/ActivityTypeSelector';
import GenericAvailabilityCodeSelector from '../../general/AvailabilityCodeSelector';
import SkillIcon from '../../general/SkillIcon';
import { AlertWith, isLongerThan } from './utils';
import { sortBy } from 'lodash';

const WarningField = ({ warning }) => {
  if (!warning) return null;
  return (
    <Form.Group controlId="warningField" as={Row} className="mb-3">
      <Form.Label column sm={3} />
      <Col sm={6}>
        <AlertWith warning={warning} />
      </Col>
    </Form.Group>
  );
};

export const NoActivityTypeWarning = ({ report }) =>
  report.activity_type_ids.length === 0 && <WarningField warning={I18n.t('activity_reports.empty_type')} />;

export const LongActivityWarning = ({ report }) =>
  isLongerThan(6, report.start_time, report.end_time) && (
    <WarningField warning={I18n.t('activity_reports.long_activity')} />
  );

export const TextField = props => (
  <Form.Group as={Row} className="mb-3" controlId={`${props.name}TextField`}>
    <Form.Label column sm={3}>
      {props.label}
    </Form.Label>
    <Col sm={6}>
      <Form.Control
        type="text"
        name={props.name}
        value={props.value}
        onChange={e => props.onChange({ [props.name]: e.target.value })}
        disabled={props.disabled}
      />
    </Col>
  </Form.Group>
);

export const DescriptionField = props => (
  <Form.Group as={Row} className="mb-3" controlId="descriptionField">
    <Form.Label column sm={3}>
      {I18n.t('activerecord.attributes.incidents/activity_report.description')}
    </Form.Label>
    <Col sm={6}>
      <Form.Control
        as="textarea"
        name="description"
        value={props.value}
        onChange={e => props.onChange({ description: e.target.value })}
      />
    </Col>
  </Form.Group>
);

export const PeriodSelectorField = props => {
  if (!props.startTime) {
    return null;
  }

  return (
    <Form.Group as={Row} className="mb-3" controlId="periodSelector">
      <Form.Label column sm={3}>
        {I18n.t('attributes.period')}
      </Form.Label>
      <Col sm={6}>
        <PeriodSelector startTime={props.startTime} endTime={props.endTime} onChange={props.onChange} />
      </Col>
    </Form.Group>
  );
};

export const PriorityButtons = ({ selected, handleChange }) => (
  <ButtonGroup className="priority-selector">
    {['prio1', 'prio2', 'prio3'].map(p => (
      <ToggleButton
        key={p}
        id={p}
        type="radio"
        variant={selected === p ? 'primary' : 'outline-primary'}
        checked={selected === p}
        onClick={() => handleChange(p)}
      >
        {I18n.t(`classy_enum.incidents/priority.${p}`)}
      </ToggleButton>
    ))}
    <ToggleButton
      id="prio-none"
      type="radio"
      variant={selected ? 'outline-primary' : 'primary'}
      checked={!selected}
      onClick={() => handleChange(null)}
    >
      {I18n.t('none')}
    </ToggleButton>
  </ButtonGroup>
);

export const PrioritySelector = props => (
  <Form.Group as={Row} className="mb-3" controlId="prioritySelector">
    <Form.Label column sm={3}>
      {I18n.t('activerecord.attributes.incidents/incident.priority')}
    </Form.Label>

    <Col sm={6}>
      <PriorityButtons selected={props.active} handleChange={prio => props.setActive({ prio })} />
    </Col>
  </Form.Group>
);

export const TypeSelector = ({ eligibleTypes, selectedTypeIds, onChange }) => {
  if (Object.entries(eligibleTypes).length === 0) return null;
  selectedTypeIds = selectedTypeIds || [];
  const selectedTypes = Object.values(eligibleTypes).filter(t => selectedTypeIds.includes(t.id));
  return (
    <Form.Group as={Row} className="mb-3" controlId="formHorizontalActivityTypes">
      <Form.Label column sm={3}>
        {I18n.t('activerecord.attributes.incidents/activity_report.activity_types')}
      </Form.Label>
      <Col sm={6}>
        <ActivityTypeSelector
          activityTypes={eligibleTypes}
          selectedActivityTypes={selectedTypes}
          handleChange={events => onChange({ activity_type_ids: events.map(e => e.value) })}
        />
      </Col>
    </Form.Group>
  );
};

export const AvailabilityCodeSelector = props => {
  if (props.availabilityCodes.length === 0) return null;
  const selectedCodeIds = props.selectedCodeIds || [];
  const selectedCodes = props.availabilityCodes.filter(t => selectedCodeIds.includes(t.id));
  return (
    <Form.Group as={Row} className="mb-3" controlId="formHorizontalAvailabilityCodes">
      <Form.Label column sm={3}>
        {I18n.t('activerecord.models.schedule/availability_code.one')}
      </Form.Label>

      <Col sm={6}>
        <GenericAvailabilityCodeSelector
          loading={props.loading}
          selectedAvailabilityCodes={selectedCodes}
          availabilityCodes={props.availabilityCodes}
          onChange={props.onChange}
          hideExistingIntervalsWarning={true}
          customInfoComponent={props.customInfoComponent}
          multi={props.multi}
        />
      </Col>
    </Form.Group>
  );
};

export const StatusButton = ({ style, data, status, icon, title }) => (
  <Button
    variant={data.attendance.reported_status === status ? `${style}` : `outline-${style}`}
    title={title}
    onClick={() => data.saveAttendance(data.attendance.id, { reported_status: status })}
  >
    {icon}
  </Button>
);

const SkillButton = ({ attendance, skill, saveAttendance }) => {
  const assignedSkills = attendance.assigned_skill_ids;
  const isActiveSkill = assignedSkills.includes(skill.id);
  const newValues = isActiveSkill ? assignedSkills.filter(id => id !== skill.id) : [...assignedSkills, skill.id];
  return (
    <ToggleButton
      variant={isActiveSkill ? 'primary' : 'outline-primary'}
      checked={isActiveSkill}
      onClick={() => saveAttendance(attendance.id, { assigned_skill_ids: newValues })}
    >
      <SkillIcon skill={skill} />
    </ToggleButton>
  );
};

export const SkillSelector = ({ attendance, eligibleSkills, saveAttendance }) => (
  <ButtonGroup size="sm" className="skill-selector">
    {sortBy(eligibleSkills, 'order').map(skill => (
      <SkillButton key={skill.id} attendance={attendance} skill={skill} saveAttendance={saveAttendance} />
    ))}
  </ButtonGroup>
);
